import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Mascot from "../components/Mascot";
import DailyChallenge from "../components/DailyChallenge";
import WheelOfRewards from "../components/WheelOfRewards";
import sfx from "../game/sfx";
import { Copy, Flame, Trophy, Coins, Award, Volume2, VolumeX, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [history, setHistory] = useState([]);
  const [ref, setRef] = useState(null);
  const [wheel, setWheel] = useState(null);
  const [wheelOpen, setWheelOpen] = useState(false);
  const [sfxOn, setSfxOn] = useState(sfx.isEnabled());

  useEffect(() => {
    let ignore = false;
    api.get("/games/history").then(({ data }) => {
      if (!ignore) setHistory(data.sessions || []);
    });
    api.get("/referral/stats").then(({ data }) => {
      if (!ignore) setRef(data);
    });
    api.get("/wheel/status").then(({ data }) => {
      if (!ignore) {
        setWheel(data);
        // Auto-open when the user is newly eligible (streak just reached milestone).
        if (data?.eligible) setWheelOpen(true);
      }
    }).catch(() => {});
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (user?.sfx_enabled === false) {
      sfx.setEnabled(false);
      setSfxOn(false);
    } else if (user?.sfx_enabled === true) {
      sfx.setEnabled(true);
      setSfxOn(true);
    }
  }, [user?.sfx_enabled]);

  if (!user) return null;

  const copyRef = async () => {
    if (!ref?.referral_link) return;
    await navigator.clipboard.writeText(ref.referral_link);
    toast.success("Referral link copied");
  };

  const toggleSfx = async () => {
    const next = !sfxOn;
    sfx.setEnabled(next);
    setSfxOn(next);
    if (next) sfx.play("coin");
    try {
      await api.post("/auth/settings", { sfx_enabled: next });
      await refreshUser();
    } catch (_) { /* storage persists locally anyway */ }
  };

  const onWheelClose = async () => {
    setWheelOpen(false);
    try {
      const { data } = await api.get("/wheel/status");
      setWheel(data);
    } catch (_) { /* noop */ }
  };

  return (
    <Layout>
      <div className="px-4 py-5 flex flex-col gap-5" data-testid="profile-page">
        <section className="cb-card p-5 flex items-center gap-4 cb-rise">
          <Mascot size={80} skin={user.active_skin} />
          <div className="flex-1 min-w-0">
            <h2
              className="font-pixel text-[13px] text-glow-green truncate"
              data-testid="profile-username"
            >
              {user.username}
            </h2>
            <p className="text-[11px] text-[#8b95a9] truncate">{user.email}</p>
            <div className="flex gap-2 mt-2">
              <span className="cb-pill">Rank · {user.role}</span>
              <span className="cb-pill border-[rgba(247,147,26,0.5)] text-[#f7931a] bg-[rgba(247,147,26,0.08)]">
                <Flame size={9} className="inline mr-1" />
                {user.streak_days}d streak
              </span>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-2 gap-3">
          <Stat
            icon={Coins}
            color="#f7931a"
            label="BOOM CREDITS"
            value={user.boom_credits}
            testid="stat-bc"
          />
          <Stat
            icon={Trophy}
            color="#2ecc71"
            label="PERSONAL BEST"
            value={user.personal_best}
            testid="stat-best"
          />
          <Stat
            icon={Coins}
            color="#2e75b6"
            label="TOTAL EARNED"
            value={user.total_earned}
            testid="stat-earned"
          />
          <Stat
            icon={Coins}
            color="#ff6b00"
            label="CONVERTED"
            value={user.total_converted}
            testid="stat-converted"
          />
        </section>

        <section className="flex flex-wrap gap-2" data-testid="profile-actions">
          <button
            type="button"
            onClick={toggleSfx}
            data-testid="sfx-toggle-btn"
            className="cb-pill flex items-center gap-2"
            style={{
              borderColor: sfxOn ? "rgba(46,204,113,0.5)" : "rgba(255,82,82,0.4)",
              color: sfxOn ? "#2ecc71" : "#ff5252",
            }}
          >
            {sfxOn ? <Volume2 size={12} /> : <VolumeX size={12} />}
            SFX {sfxOn ? "ON" : "OFF"}
          </button>

          {user.academy_certificate && (
            <span
              className="cb-pill flex items-center gap-2"
              style={{ borderColor: "rgba(46,117,182,0.6)", color: "#2e75b6" }}
              data-testid="academy-cert-badge"
            >
              <Award size={12} />
              ACADEMY CERTIFIED
            </span>
          )}

          {wheel?.eligible && (
            <button
              type="button"
              onClick={() => setWheelOpen(true)}
              data-testid="wheel-open-btn"
              className="cb-pill flex items-center gap-2 animate-pulse"
              style={{ borderColor: "rgba(247,147,26,0.8)", color: "#f7931a" }}
            >
              <Sparkles size={12} />
              SPIN WHEEL (Day {wheel.eligible_milestone})
            </button>
          )}
        </section>

        <DailyChallenge />

        {wheelOpen && <WheelOfRewards onClose={onWheelClose} />}

        <section className="cb-card p-4">
          <h3 className="font-pixel text-[10px] text-[#2e75b6] text-glow-blue mb-3">
            ▼ REFERRAL
          </h3>
          {ref ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-[#8b95a9]">CODE</span>
                <span
                  className="font-pixel text-[12px] text-[#f7931a]"
                  data-testid="referral-code"
                >
                  {ref.referral_code}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-[#8b95a9]">FRIENDS</span>
                <span className="font-mono text-[12px]">
                  {ref.friends_referred}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-[#8b95a9]">BC EARNED</span>
                <span className="font-mono text-[12px] text-[#2ecc71]">
                  {ref.bc_earned}
                </span>
              </div>
              <button
                onClick={copyRef}
                data-testid="copy-referral-btn"
                className="cb-neon-btn mt-2"
              >
                <Copy size={11} className="inline mr-2" /> Copy Link
              </button>
            </div>
          ) : (
            <p className="text-[11px] text-[#8b95a9]">Loading...</p>
          )}
        </section>

        <section>
          <h3 className="font-pixel text-[10px] text-[#2ecc71] mb-3">
            ▼ RECENT GAMES
          </h3>
          <div className="flex flex-col gap-2">
            {history.length === 0 && (
              <p className="text-[11px] text-[#8b95a9]">
                No games yet — go play!
              </p>
            )}
            {history.slice(0, 10).map((h) => (
              <div
                key={h.id}
                className="cb-card p-3 flex items-center justify-between"
                data-testid={`history-row-${h.id}`}
              >
                <div>
                  <div className="font-mono text-[12px]">Score {h.score}</div>
                  <div className="text-[9px] text-[#8b95a9]">
                    {h.distance}m · {h.blocks_destroyed} blocks ·{" "}
                    {h.coins_collected} coins
                  </div>
                </div>
                <div className="text-[#f7931a] font-pixel text-[11px]">
                  +{h.boom_credits_earned}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}

function Stat({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="cb-card p-4 flex flex-col gap-1" data-testid={testid}>
      <Icon size={16} style={{ color }} />
      <div className="text-[9px] text-[#8b95a9] font-pixel mt-1">{label}</div>
      <div className="font-mono text-[16px] text-white">{value ?? 0}</div>
    </div>
  );
}
